import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { jobsApi } from "@/lib/admin-api";
import type { JobTail } from "@/lib/jobs-types";
import { EMPTY_TAIL, applyTailPage, type TailView } from "@/lib/run-tail";
import { usePollWhile } from "@/hooks/use-poll-while";
import { useLatestOnly } from "@/hooks/use-latest-only";
import { isAbortError, useBoundedRequest } from "@/hooks/use-bounded-request";

/** The live tail's cadence: one multiplexed `tail` request every 2 s under conduit-api's `admin-tail` policy. */
export const TAIL_POLL_INTERVAL_MS = 2000;
/** Rows per tail page — conduit-api's `JobsController.TailMaxLimit`, so a late opener drains in the fewest pages. */
export const TAIL_PAGE_LIMIT = 1000;
/** Hard deadline on any single tail request — ten poll cycles, the runs tabs' bound (`use-bounded-request`). */
export const TAIL_STALE_REQUEST_MS = TAIL_POLL_INTERVAL_MS * 10;

/**
 * Keeps one run's tail current until the tail reports `finished` (sc-114197). Composes the runs tabs'
 * three hooks exactly as `SimpleRunTab` does:
 *
 * - **Open** (mount): the visible fetch — loading flag, and one toast on failure.
 * - **Ticks**: silent 2 s refreshes — a 429, network error or timeout is swallowed. The interval arms only
 *   once the open settles: a tick begun while a slow open is pending would supersede the open's token, and
 *   the open's later failure would then lose its one toast.
 * - **Cursor**: held in a ref, so every request resumes from the last *committed* page.
 *
 * Stop-polling keys on the response's `finished` flag alone, never on the grid's Hangfire state, so the
 * last batch is always shown. `nowMs` advances only on a committed page, so a failing tick never ages the
 * view's liveness notices.
 */
export function useRunTail(jobId: string): TailView & { isLoading: boolean; nowMs: number } {
  const [view, setView] = useState<TailView>(EMPTY_TAIL);
  const [isLoading, setIsLoading] = useState(true);
  const [openSettled, setOpenSettled] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const viewRef = useRef<TailView>(EMPTY_TAIL);

  // The open and every tick fetch the same page; only the newest may commit.
  const latest = useLatestOnly();
  // …and each is bounded, so a hung request settles instead of accumulating.
  const { run } = useBoundedRequest(TAIL_STALE_REQUEST_MS);

  const fetchPage = useCallback(
    (signal: AbortSignal) => jobsApi.tail(jobId, viewRef.current.cursor, TAIL_PAGE_LIMIT, signal),
    [jobId]
  );

  const commit = useCallback((page: JobTail) => {
    const next = applyTailPage(viewRef.current, page, TAIL_PAGE_LIMIT);
    viewRef.current = next; // moves the request cursor
    setView(next);
    setNowMs(Date.now());
  }, []);

  /** Visible fetch: loading flag + one toast. Mount only. */
  const open = useCallback(async () => {
    const token = latest.begin();
    setIsLoading(true);
    try {
      const page = await run(fetchPage);
      if (latest.claimCommit(token)) commit(page);
    } catch (err) {
      if (latest.isCurrent(token) && !isAbortError(err)) {
        toast.error(err instanceof Error ? err.message : "Failed to load the run log");
      }
    } finally {
      if (latest.isCurrent(token)) setIsLoading(false);
      setOpenSettled(true);
    }
  }, [latest, run, fetchPage, commit]);

  /** Silent fetch for polling: no loading flag, no toast. */
  const tick = useCallback(async () => {
    const token = latest.begin();
    try {
      const page = await run(fetchPage);
      if (latest.claimCommit(token)) commit(page);
    } catch {
      // Intentionally quiet — the visible open is what reports failures.
    } finally {
      // The current writer owns the loading flag on every path (see SimpleRunTab.refresh).
      if (latest.isCurrent(token)) setIsLoading(false);
    }
  }, [latest, run, fetchPage, commit]);

  useEffect(() => {
    void open();
  }, [open]);

  usePollWhile({
    enabled: openSettled && !view.finished,
    intervalMs: TAIL_POLL_INTERVAL_MS,
    onTick: tick,
  });

  return { ...view, isLoading, nowMs };
}
